import React, {
  FunctionComponent,
  ReactElement,
  ReactNode,
  useEffect,
} from "react";
import { useLocation } from "react-router-dom";
import { Box, Tab, Tabs as TabsMui } from "@mui/material";
import { useGetIntensityEmbedUrl } from "hooks";
import { ExportButton } from "components/buttons";

export const DashboardTabs: FunctionComponent<{
  tabs: {
    icon?: ReactElement;
    label: ReactNode;
    content: ReactNode;

    isExternalLink?: boolean;
  }[];
  selectedBtnOption?: string;
  intensity?: boolean;
  refrences?: boolean;
  onTabChange?: Function;
  textColor?: string;
  dashboard?: boolean;
  showExportButton?: boolean;
  exportDataTitle?: string;
  exportFunction?: () => Promise<any>;
}> = ({
  tabs,
  selectedBtnOption,
  intensity,
  onTabChange,
  refrences,
  dashboard = false,
  textColor = "#054254",
  showExportButton,
  exportDataTitle = "export",
  exportFunction,
}) => {
  const [value, setValue] = React.useState<number>(0);

  const { mutateAsync: getIntensityEmbedUrl } = useGetIntensityEmbedUrl();

  const location = useLocation();

  // URLSearchParams is used to parse the query string
  const queryParams = new URLSearchParams(location.search);
  const siteId = queryParams.get("s");

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    const selectedTab = tabs[newValue];

    // If it's an external link, prevent tab change - the onMouseDown handler will open the link
    if (selectedTab?.isExternalLink) {
      event.preventDefault();
      return; // Don't change the tab value, stay on current tab
    }

    setValue(newValue);
    if (onTabChange) {
      onTabChange(newValue);
    }
  };

  useEffect(() => {
    if (selectedBtnOption === "Active") {
      setValue(0);
    } else if (selectedBtnOption === "Inactive") {
      setValue(1);
    } else if (selectedBtnOption === "Renewable Energy Purchases") {
      setValue(2);
    } else if (selectedBtnOption === "Intensity") {
      setValue(3);
    } else if (selectedBtnOption === "Emissions Allocation") {
      setValue(4);
    } else {
      setValue(0);
      if (onTabChange) {
        onTabChange(0);
      }
    }
  }, [selectedBtnOption]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (siteId) {
      setValue(3);
    }
  }, [siteId]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
      }}
    >
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <TabsMui
          value={value}
          onChange={handleChange}
          textColor={intensity ? "inherit" : undefined}
          TabIndicatorProps={{
            style: {
              ...(intensity && {
                backgroundColor: `${textColor}`,
              }),
            },
          }}
          sx={{
            ".Mui-selected": {
              ...(intensity && {
                color: `${textColor} !important`,
                fontWeight: "700",
              }),
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={`tab-${index + 1}`}
              icon={tab.icon}
              label={tab.label}
              iconPosition="start"
              id={`tab-${index}`}
              aria-controls={`tabpanel-${index}`}
              onMouseDown={async (e) => {
                // Use onMouseDown to intercept before TabsMui processes the click
                if (tab.isExternalLink) {
                  e.preventDefault();
                  e.stopPropagation();
                  // Fetch the URL only when clicked, as it changes each time
                  try {
                    const response = await getIntensityEmbedUrl();
                    if (response?.embedUrl) {
                      window.open(
                        response.embedUrl,
                        "_blank",
                        "noopener,noreferrer",
                      );
                    }
                  } catch (error) {
                    console.error("Failed to get Intensity embed URL:", error);
                  }
                }
              }}
              sx={{
                minHeight: "48px",
                margin: "0 0 0 10px",
                textTransform: "none",
                fontWeight: "600",
                fontSize: "14px",
                lineHeight: "18px",
                color: "#000 !important",
              }}
            />
          ))}
        </TabsMui>
        {showExportButton && (
          <Box mb={2}>
            <ExportButton
              exportFunction={exportFunction!}
              exportTitle={exportDataTitle}
              exportText="Export Excel"
              exportingText="Loading Excel..."
              successMessage="Excel exported successfully."
              errorMessage="Error fetching data. Please try again."
              fileExtension=".xlsx"
              isZipFile={false}
              sx={{
                width: "200px",
              }}
            />
          </Box>
        )}
      </Box>
      {tabs.map((tab, index) => {
        // Don't render content for external links
        if (tab.isExternalLink) {
          return null;
        }
        return (
          <Box
            sx={
              dashboard && !refrences
                ? { display: "flex", flexWrap: "wrap", width: "100%" }
                : { mt: 3, flexGrow: 1, overflow: "auto" }
            }
            key={`tabpanel-${index + 1}`}
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
          >
            {value === index && tab.content}
          </Box>
        );
      })}
    </Box>
  );
};
